/**
 * /attendance — screen 3b, every week of the season in one list.
 *
 * Three row states, and the difference between them is the whole screen:
 *   upcoming   editable tri-state, `UPCOMING` marker on the current week
 *   answered   the same control with the chosen side filled
 *   past       read-only at 72% opacity with a ✓ marker (D22 — a week is
 *              history from Monday 09:00 Pacific, and `editable` says so)
 *
 * Past weeks are collapsed behind "Show past weeks" so the top of the list is
 * always the decision you can still make.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { AttendanceStatus, SeasonAttendanceWeek } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, GET, writeOrQueue } from '../lib/api'
import {
  Body, Button, Card, Header, Notice, Screen, Segmented, SkeletonCard, Spacer,
} from '../ui'

// ── formatting ──────────────────────────────────────────────────────────────

const WEEKDAY_SHORT = new Intl.DateTimeFormat(undefined, { weekday: 'short' })
const MONTH_DAY_SHORT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })

/** Noon local on a "YYYY-MM-DD" — never drifts across a day boundary. */
function dateMs(dateStr: string): number | null {
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10))
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d, 12, 0, 0).getTime()
}

/** "Sun Sep 27" */
function gatheringShort(dateStr: string | null): string | null {
  if (!dateStr) return null
  const at = dateMs(dateStr)
  if (at === null) return null
  return `${WEEKDAY_SHORT.format(at)} ${MONTH_DAY_SHORT.format(at)}`
}

/** "Wk 3" from a week label of "Week 3" (or whatever the calendar calls it). */
function shortWeekLabel(label: string): string {
  return label.replace(/^Week\s+/i, 'Wk ')
}

// ── wire shapes ─────────────────────────────────────────────────────────────

interface SeasonResponse {
  year: number
  weeks: SeasonAttendanceWeek[]
}

interface HistoryResponse {
  year: number
  weeksAttended: number
  weeksElapsed: number
  weeksTotal: number
  streak: number
}

// ── screen ──────────────────────────────────────────────────────────────────

export default function Attendance() {
  const { state, toast, refresh } = useStore()
  const navigate = useNavigate()

  const year = state?.week?.seasonYear ?? new Date().getFullYear()

  const [weeks, setWeeks] = useState<SeasonAttendanceWeek[] | null>(null)
  const [history, setHistory] = useState<HistoryResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [showPast, setShowPast] = useState(false)
  const [busyWeek, setBusyWeek] = useState<string | null>(null)

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const season = await GET<SeasonResponse>(`/api/attendance/season?year=${year}`, signal)
      setWeeks(season.weeks)
      setErr(null)
    } catch (e) {
      // Keep the last-known-good list; say so rather than blanking the screen.
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t refresh the season.')
    }
    try {
      setHistory(await GET<HistoryResponse>(`/api/attendance/history?year=${year}`, signal))
    } catch {
      setHistory(null)
    }
  }, [year])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  // ── writes ────────────────────────────────────────────────────────────────

  const answer = useCallback(
    async (weekId: string, next: AttendanceStatus | null) => {
      setBusyWeek(weekId)
      // Optimistic: the row flips now, the list reconciles on the refetch.
      setWeeks((prev) => prev?.map((w) => (w.week.id === weekId ? { ...w, mine: next } : w)) ?? prev)
      try {
        const res = await writeOrQueue(
          `/api/attendance/${weekId}`,
          'PUT',
          { status: next, guests: 0 },
          next === null ? 'Cleared an answer' : next === 'in' ? "You're in" : "You're out",
        )
        if (res === null) toast('Saved. It will send when you’re back online')
        else await load()
      } catch (e) {
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t save that answer.')
        await load()
      } finally {
        setBusyWeek(null)
      }
    },
    [load, toast],
  )

  /*
   * The two bulk controls are gone.
   *
   * "I'm in for the rest of the season" and "I'm out through a date" both
   * wrote an answer to every remaining week at once, which is a promise
   * nobody in this group was ever actually making: the reason to open this
   * screen is that one particular Sunday changed. What they reliably produced
   * was a season of "in" that nobody had thought about and that stopped
   * meaning anything.
   *
   * `PUT /api/attendance/bulk` is untouched and still works; nothing in the
   * app calls it.
   */

  // ── render ────────────────────────────────────────────────────────────────

  const upcoming = weeks?.filter((w) => w.editable) ?? []
  const past = weeks?.filter((w) => !w.editable) ?? []

  const attended = history?.weeksAttended ?? past.filter((w) => w.attended).length
  const totalWeeks = history?.weeksTotal ?? weeks?.length ?? 0
  const streak = history?.streak ?? 0

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        logo={false}
        left={
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            style={{ fontSize: 17, fontWeight: 700, minHeight: 44, minWidth: 44, textAlign: 'left' }}
          >
            ← Attendance
          </button>
        }
        right={<span className="mono" style={{ fontSize: 11, color: 'var(--dim)' }}>{year} season</span>}
      />

      <Body>
        {weeks === null ? (
          <>
            <div className="skeleton-bar" style={{ width: '45%', height: 22, marginBottom: 18 }} />
            <SkeletonCard lines={3} />
            <Spacer h={12} />
            <SkeletonCard lines={3} />
            <Spacer h={12} />
            <SkeletonCard lines={2} />
          </>
        ) : (
          <>
            {err ? <><Notice tone="warn">{err}</Notice><Spacer h={16} /></> : null}

            <h1 className="big-title" style={{ margin: 0 }}>
              {attended} of {totalWeeks} weeks
            </h1>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 4 }}>
              {streak > 0 ? `${streak}-week streak` : 'No streak yet'}
            </div>

            {weeks.length === 0 ? (
              <Card style={{ marginTop: 18, padding: '26px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>No weeks on the calendar yet.</div>
                <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
                  The season schedule hasn’t synced. Check back shortly.
                </div>
              </Card>
            ) : null}

            {upcoming.map((w, i) => (
              <WeekCard
                key={w.week.id}
                week={w}
                first={i === 0}
                busy={busyWeek === w.week.id}
                onAnswer={(next) => void answer(w.week.id, next)}
              />
            ))}

            {showPast
              ? past.slice().reverse().map((w) => <PastWeekCard key={w.week.id} week={w} />)
              : null}

            {past.length ? (
              <button
                onClick={() => setShowPast((v) => !v)}
                aria-expanded={showPast}
                style={{
                  display: 'block', width: '100%', textAlign: 'center', padding: 13, marginTop: 10,
                  fontSize: 13, fontWeight: 600, color: 'var(--bright)', minHeight: 44,
                }}
              >
                {showPast ? 'Hide past weeks' : 'Show past weeks'}
              </button>
            ) : null}
          </>
        )}
      </Body>

    </Screen>
  )
}

// ── rows ────────────────────────────────────────────────────────────────────

function WeekCard({
  week, first, busy, onAnswer,
}: {
  week: SeasonAttendanceWeek
  first: boolean
  busy: boolean
  onAnswer: (next: AttendanceStatus | null) => void
}) {
  const when = gatheringShort(week.week.gatheringDate)
  return (
    <Card style={{ marginTop: first ? 18 : 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>
          {shortWeekLabel(week.week.label)}{when ? ` · ${when}` : ''}
        </span>
        {week.isCurrent ? (
          <span className="mono" style={{ fontSize: 10, color: 'var(--bright)', flex: '0 0 auto', letterSpacing: '.12em' }}>
            UPCOMING
          </span>
        ) : null}
      </div>

      <div style={{ marginTop: 12, opacity: busy ? 0.6 : 1 }}>
        <Segmented<AttendanceStatus>
          size="sm"
          options={[{ value: 'in', label: "I'm in" }, { value: 'out', label: "Can't make it" }]}
          value={week.mine}
          onChange={(v) => { if (!busy) onAnswer(v === week.mine ? null : v) }}
        />
      </div>

      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 12, fontSize: 12, color: 'var(--dim)' }}
        aria-live="polite"
      >
        {/* Hosting is gone from the app entirely. The volunteer prompt was an
            unanswered chore that sat on screen every week of the season, and
            "Host: not set" was that same chore phrased as a failure. */}
        <span>{week.in} in · {week.out} out · {week.unknown} no answer</span>
      </div>
    </Card>
  )
}

function PastWeekCard({ week }: { week: SeasonAttendanceWeek }) {
  const when = gatheringShort(week.week.gatheringDate)
  const marker = week.attended
    ? '✓ you came'
    : week.mine === 'out' ? 'you were out' : 'no answer'
  return (
    <Card style={{ marginTop: 12, opacity: 0.72 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>
          {shortWeekLabel(week.week.label)}{when ? ` · ${when}` : ''}
        </span>
        <span style={{ fontSize: 12, flex: '0 0 auto', color: week.attended ? 'var(--bright)' : 'var(--dim)' }}>
          {marker}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 10, fontSize: 12, color: 'var(--dim)' }}>
        <span>{week.in} attended</span>
      </div>
    </Card>
  )
}
